// The coach's presence: a scope line in a recessed well.
//
//   idle       a calm, slow line
//   attention  the same line with a gentle pulse, plus a count
//   thinking   an active scope while the assistant works
//
// Under reduced motion the line is drawn once and stays still; attention
// shows only the count. Colours come from CSS (styles/coach-companion.css,
// styles/skins/instrument-coach.css): amber in Instrument, ice-cyan in Polar.
import React, { useId } from "react";
import { usePrefersReducedMotion } from "../ui/motion";
import { glyphLabel } from "./coachFormat";

const SIZES = {
  sm: { w: 40, h: 20 },
  md: { w: 64, h: 28 },
  lg: { w: 132, h: 44 },
};

// One tile of the line, drawn twice side by side so the scroll loops
// without a seam. Every period divides the tile width.
const wavePath = (w, h, state) => {
  const mid = h / 2;
  const amp = state === "thinking" ? h * 0.3 : h * 0.12;
  const harmonics =
    state === "thinking"
      ? [
          [1, 2],
          [0.45, 5],
          [0.25, 9],
        ]
      : [[1, 2]];
  const steps = 64;
  const points = [];
  for (let i = 0; i <= steps * 2; i += 1) {
    const x = (i / steps) * w;
    const t = x / w;
    const y = harmonics.reduce(
      (sum, [weight, cycles]) =>
        sum + weight * Math.sin(2 * Math.PI * cycles * t),
      0,
    );
    const norm = harmonics.reduce((sum, [weight]) => sum + weight, 0);
    points.push(`${x.toFixed(2)},${(mid - (amp * y) / norm).toFixed(2)}`);
  }
  return `M${points.join("L")}`;
};

/**
 * @param {{state?: "idle" | "attention" | "thinking", count?: number,
 *   size?: "sm" | "md" | "lg", decorative?: boolean, className?: string}} props
 */
const CoachGlyph = ({
  state = "idle",
  count = 0,
  size = "sm",
  decorative = true,
  className = "",
}) => {
  const reduced = usePrefersReducedMotion();
  const uid = useId().replace(/:/g, "");
  const { w, h } = SIZES[size] || SIZES.sm;
  const showCount = state === "attention" && count > 0;
  const motion = reduced ? "off" : "on";

  return (
    <span
      className={`nx-glyph ${className}`}
      data-state={state}
      data-size={size}
      data-motion={motion}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : glyphLabel(state, count)}
      aria-hidden={decorative ? "true" : undefined}
    >
      <svg
        className="nx-glyph-well"
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        focusable="false"
      >
        <defs>
          <linearGradient id={`nxg-fade-${uid}`} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#fff" stopOpacity="0" />
            <stop offset="0.18" stopColor="#fff" stopOpacity="1" />
            <stop offset="0.82" stopColor="#fff" stopOpacity="1" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          <mask id={`nxg-mask-${uid}`}>
            <rect width={w} height={h} fill={`url(#nxg-fade-${uid})`} />
          </mask>
        </defs>
        <rect
          className="nx-glyph-bg"
          x="0.5"
          y="0.5"
          width={w - 1}
          height={h - 1}
          rx={Math.min(6, h / 3)}
        />
        <line
          className="nx-glyph-grid"
          x1="2"
          x2={w - 2}
          y1={h / 2}
          y2={h / 2}
        />
        <g mask={`url(#nxg-mask-${uid})`}>
          <g className="nx-glyph-scroll" style={{ "--nx-glyph-w": `${w}px` }}>
            <path className="nx-glyph-glow" d={wavePath(w, h, state)} />
            <path className="nx-glyph-line" d={wavePath(w, h, state)} />
          </g>
        </g>
      </svg>
      {showCount ? (
        <span className="nx-glyph-count" aria-hidden="true">
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
    </span>
  );
};

export default CoachGlyph;
